import React, { useEffect } from 'react';
import { Modal, Form, InputNumber, Input, Radio, Select, Space, Row, Col } from 'antd';

/**
 * Utility function to generate row/column name arrays based on naming convention.
 */
export const generateNames = (convention, count, defaultPrefix = 'Item') => {
  const n = parseInt(count, 10) || 0;
  if (n <= 0) return [];

  switch (convention) {
    case 'abc':
      return Array.from({ length: n }, (_, i) => String.fromCharCode(97 + (i % 26)));
    case 'ABC':
      return Array.from({ length: n }, (_, i) => String.fromCharCode(65 + (i % 26)));
    case '123':
      return Array.from({ length: n }, (_, i) => `${i + 1}`);
    case 'months': {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return Array.from({ length: n }, (_, i) => months[i % 12]);
    }
    case 'default':
    default:
      return Array.from({ length: n }, (_, i) => `${defaultPrefix} ${i + 1}`);
  }
};

const UniversalModuleModal = ({ isVisible, onClose, onConfirm, config, initialValues }) => {
  const [form] = Form.useForm();

  // Pre-populate form when modal opens or initialValues/config changes
  useEffect(() => {
    if (isVisible && config) {
      const defaults = {};

      // Fill defaults from config schema
      config.fields?.forEach((f) => {
        if (f.default !== undefined) {
          defaults[f.name] = f.default;
        }
      });

      if (config.hasRowNaming) defaults.rowNaming = config.defaultRowNaming || 'default';
      if (config.hasColNaming) defaults.colNaming = config.defaultColNaming || 'default';

      // Merge with passed initialValues (e.g. from API or edit mode)
      form.setFieldsValue({ ...defaults, ...initialValues });
    }
  }, [isVisible, config, initialValues, form]);

  const values = Form.useWatch([], form) || {};

  const getRowCount = () => {
    return values.numRows || values.itemCount || values.rowCount || values.numAlternatives || values.numFactors || values.numSources || values.numTasks || values.numPoints || values.numSamples || values.numItems || values.periods || values.numProducts || values.numElements || values.max_system_size || values.numCategories || 0;
  };

  const getColCount = () => {
    return values.numCols || values.colCount || values.scenariosCount || values.numCriteria || values.numLocations || values.numDestinations || values.numCycles || 0;
  };

  if (!config) return null;

  const handleOk = () => {
    form.validateFields().then((values) => {
      const output = { ...values };

      // Generate Row Names if configured
      if (config.hasRowNaming) {
        const rowCount = getRowCount();
        if (values.rowNaming === 'custom') {
          output.rowNames = values.customRowNames || [];
          for (let i = 0; i < rowCount; i++) {
            if (!output.rowNames[i]) {
              output.rowNames[i] = `${config.rowPrefix || 'Row'} ${i + 1}`;
            }
          }
        } else {
          output.rowNames = generateNames(values.rowNaming, rowCount, config.rowPrefix || 'Item');
        }
      }

      // Generate Column Names if configured
      if (config.hasColNaming) {
        const colCount = getColCount();
        if (values.colNaming === 'custom') {
          output.colNames = values.customColNames || [];
          for (let i = 0; i < colCount; i++) {
            if (!output.colNames[i]) {
              output.colNames[i] = `${config.colPrefix || 'Column'} ${i + 1}`;
            }
          }
        } else {
          output.colNames = generateNames(values.colNaming, colCount, config.colPrefix || 'Scenario');
        }
      }

      onConfirm(output);
      form.resetFields();
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const rowCount = getRowCount();
  const colCount = getColCount();

  return (
    <Modal
      title={config.title}
      open={isVisible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={config.width || 520}
      okText={config.okText || 'OK'}
      cancelText="Cancel"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          {config.fields?.map((field) => (
            <Col span={field.span || 24} key={field.name}>
              <Form.Item
                name={field.name}
                label={field.label}
                rules={field.rules || [{ required: field.required !== false, message: `Please enter ${field.label}` }]}
              >
                {field.type === 'number' && (
                  <InputNumber
                    min={field.min}
                    max={field.max}
                    step={field.step || 1}
                    precision={field.precision}
                    style={{ width: '100%' }}
                    placeholder={field.placeholder || `Enter ${field.label}`}
                  />
                )}
                {field.type === 'text' && (
                  <Input placeholder={field.placeholder || `Enter ${field.label}`} />
                )}
                {field.type === 'select' && (
                  <Select placeholder={field.placeholder || `Select ${field.label}`}>
                    {field.options?.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                )}
                {field.type === 'radio' && (
                  <Radio.Group>
                    <Space direction={field.direction || 'horizontal'}>
                      {field.options?.map((opt) => (
                        <Radio key={opt.value} value={opt.value}>
                          {opt.label}
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                )}
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Row Naming Radio Selector */}
        {config.hasRowNaming && (
          <>
            <Form.Item label={config.rowNamingLabel || 'Row Names'} name="rowNaming">
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value="default">{config.rowPrefix || 'Item'} 1, {config.rowPrefix || 'Item'} 2...</Radio>
                  <Radio value="ABC">A, B, C...</Radio>
                  <Radio value="123">1, 2, 3...</Radio>
                  {config.allowMonths && <Radio value="months">Jan, Feb, Mar...</Radio>}
                  <Radio value="custom">Other (Custom Names)</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            {values.rowNaming === 'custom' && (
              <div style={{ marginTop: 10, marginBottom: 15, maxHeight: 150, overflowY: 'auto', padding: '10px', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                <p style={{ fontWeight: '500', marginBottom: 5 }}>Enter Custom Row Names:</p>
                {Array.from({ length: rowCount }).map((_, index) => (
                  <Form.Item
                    key={index}
                    name={['customRowNames', index]}
                    rules={[{ required: true, message: `Please enter name for row ${index + 1}` }]}
                    style={{ marginBottom: 5 }}
                  >
                    <Input placeholder={`${config.rowPrefix || 'Row'} ${index + 1}`} />
                  </Form.Item>
                ))}
              </div>
            )}
          </>
        )}

        {/* Column Naming Radio Selector */}
        {config.hasColNaming && (
          <>
            <Form.Item label={config.colNamingLabel || 'Column Names'} name="colNaming">
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value="default">{config.colPrefix || 'Scenario'} 1, {config.colPrefix || 'Scenario'} 2...</Radio>
                  <Radio value="ABC">A, B, C...</Radio>
                  <Radio value="123">1, 2, 3...</Radio>
                  {config.allowMonths && <Radio value="months">Jan, Feb, Mar...</Radio>}
                  <Radio value="custom">Other (Custom Names)</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            {values.colNaming === 'custom' && (
              <div style={{ marginTop: 10, marginBottom: 15, maxHeight: 150, overflowY: 'auto', padding: '10px', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                <p style={{ fontWeight: '500', marginBottom: 5 }}>Enter Custom Column Names:</p>
                {Array.from({ length: colCount }).map((_, index) => (
                  <Form.Item
                    key={index}
                    name={['customColNames', index]}
                    rules={[{ required: true, message: `Please enter name for column ${index + 1}` }]}
                    style={{ marginBottom: 5 }}
                  >
                    <Input placeholder={`${config.colPrefix || 'Column'} ${index + 1}`} />
                  </Form.Item>
                ))}
              </div>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default UniversalModuleModal;
